<template>
  <div class="flex w-full flex-col md:flex-row">
    <!-- Grade -->
    <div
      class="border-static-default-low flex justify-between gap-32 border-b-1 md:w-2/12 md:flex-col md:justify-normal md:gap-0"
    >
      <div :class="headingsClasses">
        <p>{{ i18n(translations.listingGradeLabel) }}</p>
      </div>
      <div
        :class="[cellClasses, 'overflow-hidden pr-12 md:overflow-auto']"
        data-test="grade-name"
      >
        <RevTag
          :label="getQuoteGradeName(listing.grade)"
          :variant="tagVariant"
        />
      </div>
    </div>
    <!-- Price to win BackBox  -->
    <div
      class="border-static-default-low flex justify-between gap-3 border-b-1 md:w-2/12 md:flex-col md:justify-normal md:gap-0"
    >
      <div :class="headingsClasses">
        <p>{{ i18n(translations.priceToWinBackboxTitle) }}</p>
        <RevTooltip position="bottom-end">
          <template #trigger="{ show, hide }">
            <RevButtonIcon
              class="mr-12 cursor-pointer"
              :disabled="false"
              :icon="IconInfo"
              variant="secondary"
              @focusin="show"
              @focusout="hide"
              @mouseleave="hide"
              @mouseover="show"
            />
          </template>
          {{ i18n(translations.priceToWinBackboxTooltip) }}
        </RevTooltip>
      </div>

      <div :class="cellClasses">
        <RevSpinner v-if="isPriceToWinLoading" class="mx-auto"></RevSpinner>
        <p v-else>
          {{ i18n.price(priceToWinBackbox) }}
        </p>
      </div>
    </div>
    <!-- BackBox price  -->
    <div
      class="border-static-default-low flex justify-between gap-32 border-b-1 md:w-1/12 md:flex-col md:justify-normal md:gap-0"
    >
      <div :class="headingsClasses">
        <p>{{ i18n(translations.listingBackBoxPriceLabel) }}</p>
      </div>

      <div :class="cellClasses">
        <p>
          {{
            i18n.price({
              amount: backboxPrice.amount,
              currency: backboxPrice.currency,
            })
          }}
        </p>
      </div>
    </div>
    <!-- Price -->
    <div
      class="border-static-default-low flex justify-between gap-32 border-b-1 md:w-2/12 md:flex-col md:justify-normal md:gap-0 md:pl-20"
    >
      <div :class="headingsClasses">
        <p>{{ i18n(translations.listingPriceLabel) }}</p>
      </div>
      <div :class="cellClasses">
        <RevInputText
          :id="`${index}-price`"
          v-model="formattedPrice"
          class="w-[7.5rem] md:w-auto"
          :has-clear-button="false"
          :label="i18n(translations.priceInputLabel, { currency })"
        />
      </div>
    </div>
    <!-- Status -->
    <div
      class="border-static-default-low flex grow justify-between gap-32 border-b-1 md:w-3/12 md:flex-col md:justify-normal md:gap-0"
    >
      <div :class="headingsClasses">
        <p>{{ i18n(translations.listingStatusLabel) }}</p>
      </div>
      <div :class="cellClasses">
        <p :class="statusMessage.classes">
          <component
            :is="statusMessage.component"
            v-if="statusMessage?.component"
            class="mr-8 hidden shrink-0 md:block"
          />
          {{ statusMessage.message }}
        </p>
      </div>
    </div>
    <!-- Online/Offline/Save buttons -->
    <div class="border-static-default-low relative border-b-1">
      <div :class="headingsClasses"></div>
      <div :class="[cellClasses, 'w-full justify-end']">
        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 z-10 grid h-64 place-content-center"
          :class="
            loadingGrades.includes(listing.grade) ? 'opacity-100' : 'opacity-0'
          "
        >
          <RevSpinner size="small" />
        </div>
        <div
          class="flex justify-between gap-4 md:flex md:justify-normal"
          :class="
            loadingGrades.includes(listing.grade) ? 'opacity-0' : 'opacity-100'
          "
        >
          <RevButton
            :disabled="isTogglePublicationStateDisabled"
            size="small"
            variant="secondary"
            @click="toggleState"
          >
            {{ publicationStateCtaLabel }}
          </RevButton>
          <RevButton
            class="ml-2"
            :disabled="isSaveListingDisabled"
            size="small"
            variant="primary"
            @click="savePrice"
          >
            {{ i18n(translations.save) }}
          </RevButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import {
  type BuyBackQuoteListing,
  BuyBackQuoteListingStates,
  type BuyBackQuotePricingEstimation,
  type PostBuyBackQuoteListing,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { LISTING_PUBLICATION_STATE } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.constants'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { tw } from '@backmarket/utils/string/tw'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInputText } from '@ds/components/InputText'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag, type Variants } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconInfo } from '@ds/icons/IconInfo'
import { IconWarning } from '@ds/icons/IconWarning'

import { usePriceToWinBackbox } from '~/scopes/buyback/composables/usePriceToWinBackbox'
import { getQuoteGradeName } from '~/scopes/buyback/pages/wishlist/QuotesWishList.utils'
import { removeDecimalsFromString } from '~/scopes/buyback/utils/buyback.utils'

import translations from './ListingLine.translations'

const props = withDefaults(
  defineProps<{
    listing: BuyBackQuoteListing
    index: number
    loadingGrades: Array<string>
    marketCode: string
    tagVariant: Variants
    isPriceToWinLoading?: boolean
    pricingEstimation?: BuyBackQuotePricingEstimation
  }>(),
  {
    isPriceToWinLoading: false,
    pricingEstimation: undefined,
  },
)

const emit = defineEmits<{
  (e: 'save-price', payload: PostBuyBackQuoteListing): void
  (e: 'toggle-state', payload: PostBuyBackQuoteListing): void
}>()

const i18n = useI18n()

const formattedPrice = ref(
  removeDecimalsFromString(props.listing.price?.amount),
)

const headingsClasses = computed(() => {
  const common = tw`h-64 body-1-bold text-primary border-b-1 border-static-default-low py-12 flex items-center`
  const display = tw`block`
  const hiddenDesktop = tw`block md:hidden`

  return props.index === 0 ? [display, common] : [hiddenDesktop, common]
})

const cellClasses = computed(() => {
  return tw`h-64 py-8 flex items-center px-2`
})

const currency = computed(() => {
  if (props.listing.price?.currency) {
    return props.listing.price?.currency
  }

  return getMarketplace(props.marketCode as MarketCountryCode)?.market
    ?.defaultCurrency
})

const { pricingEstimation, listing } = toRefs(props)
const priceToWinBackbox = usePriceToWinBackbox(
  pricingEstimation,
  listing,
  currency,
)

const backboxPrice = computed(() =>
  !props.listing.backboxPrice
    ? {
        amount: '0',
        currency: currency.value,
      }
    : props.listing.backboxPrice,
)

const isListingOnline = computed(
  () => props.listing.state === LISTING_PUBLICATION_STATE.ONLINE,
)

// TODO: Add statusMessage type
const statusMessage = computed(() => {
  const common = tw`body-1-light flex items-center text-start`
  const success = tw`text-static-success-hi`
  const danger = tw`text-static-danger-hi`
  const offline = tw`text-static-default-hi`

  if (!isListingOnline.value) {
    return {
      component: null,
      message: i18n(translations.offlineStatus),
      classes: [common, offline],
    }
  }

  if (props.listing.hasBackbox) {
    return {
      component: IconCheckInCircle,
      message: i18n(translations.backboxPriceOkMessage),
      classes: [common, success],
    }
  }

  return {
    component: IconWarning,
    message: i18n(translations.backboxPriceWarningMessage, {
      backboxPrice: i18n.price(backboxPrice.value),
    }),
    classes: [common, danger],
  }
})

// TODO: We can simplify this I think. But do we agree that formattedPrice will never be negative ? If it can be, we can either add a min value on the input, or use parseInt(formattedPrice.value, 10) <= 0
const isTogglePublicationStateDisabled = computed(
  () => !(parseInt(formattedPrice.value, 10) > 0),
)

const isSaveListingDisabled = computed(() => {
  const priceHasNotChanged =
    formattedPrice.value === props.listing.price?.amount
  const priceIsEmpty = formattedPrice.value === ''
  const priceIsNegative = parseInt(formattedPrice.value, 10) < 0

  return priceHasNotChanged || priceIsEmpty || priceIsNegative
})

const publicationStateCtaLabel = computed(() =>
  isListingOnline.value
    ? i18n(translations.turnOffline)
    : i18n(translations.turnOnline),
)

function savePrice() {
  const listingPostPayload: PostBuyBackQuoteListing = {
    grade: props.listing.grade,
    price: {
      amount: formattedPrice.value,
      currency: currency.value,
    },
  }

  emit('save-price', listingPostPayload)
}

function toggleState() {
  const state =
    props.listing.state === LISTING_PUBLICATION_STATE.ONLINE
      ? BuyBackQuoteListingStates.DISABLED
      : BuyBackQuoteListingStates.ENABLED

  const listingPostPayload: PostBuyBackQuoteListing = {
    grade: props.listing.grade,
    state,
  }

  emit('toggle-state', listingPostPayload)
}
</script>
